import {Box, Button, ButtonGroup, Card, CardContent, CardHeader, Chip, Divider, Grid, Stack, Typography} from "@mui/material";
import {BreadcrumbItem, BreadcrumbLink, Breadcrumbs, View, ViewHeader} from "@variocube/app-ui";
import React from "react";
import {useAsync} from "react-async-hook";
import {Link} from "react-router-dom";
import {CreditorInboxMutation, useCreditorApi} from "../../../api";
import {LabeledText} from "../../../component/labeled-text";
import {hasRoles, useAuthContext} from "../../../context/auth";
import {useLocalization} from "../../../i18n";
import {useNumericParam} from "../../../utils/useParam";
import {CreditorInboxes} from "./creditor-inboxes";

export function CreditorView() {
	const id = useNumericParam("id");
	const {t} = useLocalization();
	const {user} = useAuthContext();
	const {getCreditor, updateCreditorInboxes, deleteCreditorInbox, addCreditorInbox} = useCreditorApi();

	const {loading, error, result, execute: refresh} = useAsync(getCreditor, [id]);

	async function handleSaveCreditorInboxes(mutations: CreditorInboxMutation[]) {
		if (result !== undefined) {
			await updateCreditorInboxes(result.id, mutations);
			await refresh(result.id);
		}
	}

	async function handleRemoveCreditorInbox(inboxId: number) {
		if (result !== undefined) {
			await deleteCreditorInbox(result.id, inboxId);
			await refresh(result.id);
		}
	}

	async function handleAddCreditorInbox(mutation: CreditorInboxMutation) {
		if (result !== undefined) {
			await addCreditorInbox(result.id, mutation);
			await refresh(result.id);
		}
	}

	return (
		<View loading={loading} error={error}>
			<Breadcrumbs>
				<BreadcrumbItem>{t("navigation.admin")}</BreadcrumbItem>
				<BreadcrumbLink component={Link} relative="path" to={".."}>{t("creditors.plural")}</BreadcrumbLink>
				<BreadcrumbItem>{result?.company.companyName}</BreadcrumbItem>
			</Breadcrumbs>
			<ViewHeader
				title={result?.company.companyName}
				titleAdornment={
					<Chip
						label={result?.enabled ? t("common.active") : t("common.inactive")}
						color={result?.enabled ? "success" : "default"}
					/>
				}
				subTitle={
					<Stack spacing={1} direction="row" divider={<Divider orientation="vertical" flexItem />}>
						{result?.company.form && <span>{t(`signup.legalForms.${result.company.form}`)}</span>}
						{result?.company.vatIdentification && <span>{result.company.vatIdentification}</span>}
						{result?.company.commercialRegister && <span>{result.company.commercialRegister}</span>}
					</Stack>
				}
				actions={
					<ButtonGroup>
						{hasRoles(user, ["SuperUser"]) && (
							<Button variant="outlined" color="warning" component={Link} to={`merge`}>
								{t("creditors.merge.button")}
							</Button>
						)}
						<Button color="primary" variant="outlined" component={Link} to={`edit`}>
							{t("creditors.edit.title")}
						</Button>
					</ButtonGroup>
				}
			/>
			<Box>
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6}>
						<Card sx={{height: "100%"}}>
							<CardHeader title={t("signup.companyAddress")} />
							<CardContent>
								<Typography>{result?.address?.street} {result?.address?.houseNo}</Typography>
								<Typography>{result?.address?.zip} {result?.address?.city}</Typography>
								<Typography>{result?.address?.country}</Typography>
							</CardContent>
						</Card>
					</Grid>
					<Grid item xs={12} sm={6}>
						<Card sx={{height: "100%"}}>
							<CardHeader title={t("creditors.settings")} />
							<CardContent>
								<Stack spacing={2}>
									<Stack direction="row" spacing={2}>
										{result?.voxelSupportsReverseCharge && (
											<Chip label={t("creditors.voxelReverseChange")} color="info" />
										)}
										{result?.creditCardProvider && (
											<Chip label={t("creditors.creditCardProvider")} color="info" />
										)}
									</Stack>
									{result?.sapCreditorNumber && (
										<LabeledText
											label={t("creditors.sapCreditorNumber")}
											text={result?.sapCreditorNumber}
										/>
									)}
									<LabeledText
										label={t("creditors.checkManually")}
										text={
											<Chip
												color={result?.checkManually ? "success" : "default"}
												label={result?.checkManually ? t("common.yes") : t("common.no")}
											/>
										}
									/>
								</Stack>
							</CardContent>
						</Card>
					</Grid>
				</Grid>
			</Box>
			<CreditorInboxes
				inboxes={result?.inboxes ?? []}
				onAdd={handleAddCreditorInbox}
				onDelete={handleRemoveCreditorInbox}
				onEdit={handleSaveCreditorInboxes}
			/>
		</View>
	);
}
